const boutiquesBlock = (settings = {}) => {
  return {
    settings,
    loading: false,
    error: '',
    boutiques: [],
    openAccordions: new Set(),

    init() {
      this.fetchBoutiques();
    },

    get groupedBoutiques() {
      const groups = new Map();

      this.boutiques.forEach((boutique) => {
        const country = (boutique.country || 'Other').trim();

        if (!groups.has(country)) {
          groups.set(country, []);
        }

        groups.get(country).push(boutique);
      });

      return Array.from(groups.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([country, items]) => ({ country, items }));
    },

    toggleAccordion(index) {
      if (this.openAccordions.has(index)) {
        this.openAccordions.delete(index);
      } else {
        this.openAccordions.add(index);
      }
    },

    isAccordionOpen(index) {
      return this.openAccordions.has(index);
    },

    openMap(mapLink) {
      if (mapLink) {
        window.open(mapLink, '_blank');
      }
    },

    async fetchBoutiques() {
      this.loading = true;
      this.error = '';

      try {
        const reference = this.$el.dataset.metaobjectReference || this.settings.metaobject_reference || '';
        const typeCandidates = this.getTypeCandidates(reference);

        if (typeCandidates.length === 0) {
          throw new Error('Metaobject reference is required.');
        }

        const entries = await this.fetchMetaobjectEntries(typeCandidates);
        this.boutiques = entries
          .map((entry) => this.mapBoutique(entry))
          .filter((boutique) => boutique.title);
      } catch (error) {
        console.error('[BoutiquesBlock] Failed to fetch boutiques:', error);
        this.error = 'Unable to load boutiques at the moment.';
      } finally {
        this.loading = false;
      }
    },

    getTypeCandidates(reference) {
      const normalized = (reference || '').trim();
      if (!normalized) return [];

      const candidates = [normalized];

      if (normalized.includes('.')) {
        const fallback = normalized.split('.').pop().trim();
        if (fallback && !candidates.includes(fallback)) {
          candidates.push(fallback);
        }
      }

      return candidates;
    },

    mapBoutique(entry) {
      const title = this.getFieldValue(entry.fields, 'title') || '';
      const country = this.getFieldValue(entry.fields, 'country') || 'Other';
      const address = this.getFieldValue(entry.fields, 'address') || '';
      const phone = this.getFieldValue(entry.fields, 'phone') || '';
      const email = this.getFieldValue(entry.fields, 'email') || '';
      const descriptionRaw = this.getFieldValue(entry.fields, 'description') || '';
      const description = this.richTextToHtml(descriptionRaw) || descriptionRaw;
      const mapLink = this.getFieldValue(entry.fields, 'map_link') || '';
      const appointmentLink = this.getFieldValue(entry.fields, 'appointment_link') || '';

      const imageField = this.getField(entry.fields, 'featured_image');
      const image = imageField?.reference?.image
        ? {
            url: imageField.reference.image.url,
            alt: imageField.reference.image.altText || title,
            width: imageField.reference.image.width,
            height: imageField.reference.image.height
          }
        : null;

      const openingHoursRaw = this.getFieldValue(entry.fields, 'opening_hours');
      const openingHours = this.parseOpeningHours(openingHoursRaw);

      return {
        id: entry.id,
        title,
        country,
        address,
        phone,
        email,
        description,
        mapLink,
        appointmentLink,
        image,
        openingHours
      };
    },

    parseOpeningHours(rawHours) {
      if (!rawHours) return [];

      const normalized = rawHours.trim();
      if (!normalized) return [];

      try {
        // JSON array of strings: ["MONDAY:10.00AM- 9:30PM", ...] or objects: [{day, time}]
        if (normalized.startsWith('[')) {
          const parsed = JSON.parse(normalized);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => {
                if (typeof item === 'object' && item !== null && item.day && item.time) {
                  return item;
                }
                if (typeof item === 'string') {
                  return this.parseHoursLine(item);
                }
                return null;
              })
              .filter(Boolean);
          }
        }
      } catch {
        // Fall back to line-by-line parsing
      }

      // New-line separated plain text
      return normalized
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => this.parseHoursLine(line))
        .filter(Boolean);
    },

    // Splits "MONDAY:10.00AM- 9:30PM" on the first colon
    parseHoursLine(line) {
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        const day = line.substring(0, colonIndex).trim();
        const time = line.substring(colonIndex + 1).trim();
        if (day && time) return { day, time };
      }
      // Fallback: split on first whitespace boundary
      const parts = line.split(/\s+/);
      if (parts.length >= 2) {
        return { day: parts[0], time: parts.slice(1).join(' ') };
      }
      return null;
    },

    // Converts Shopify rich text JSON document to an HTML string
    richTextToHtml(jsonString) {
      if (!jsonString) return '';

      let doc;
      try {
        doc = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
      } catch {
        return jsonString;
      }

      const render = (node) => {
        if (!node) return '';
        const children = () => (node.children || []).map(render).join('');

        switch (node.type) {
          case 'root': return children();
          case 'paragraph': return `<p>${children()}</p>`;
          case 'heading': return `<h${node.level || 2}>${children()}</h${node.level || 2}>`;
          case 'list': return node.listType === 'ordered'
            ? `<ol>${children()}</ol>`
            : `<ul>${children()}</ul>`;
          case 'list-item': return `<li>${children()}</li>`;
          case 'link': return `<a href="${node.url || '#'}">${children()}</a>`;
          case 'text': {
            let t = node.value || '';
            if (node.bold) t = `<strong>${t}</strong>`;
            if (node.italic) t = `<em>${t}</em>`;
            if (node.underline) t = `<u>${t}</u>`;
            return t;
          }
          default: return node.value || children();
        }
      };

      return render(doc);
    },

    getField(fields, key) {
      return fields?.find((item) => item.key === key) || null;
    },

    getFieldValue(fields, key) {
      return this.getField(fields, key)?.value || '';
    },

    async fetchMetaobjectEntries(typeCandidates) {
      const accessToken = window.themeSettings?.storefront_access_token;

      if (!accessToken) {
        throw new Error('Storefront access token not found in window.themeSettings');
      }

      const query = `
        query GetBoutiqueMetaobjects($type: String!) {
          metaobjects(type: $type, first: 250) {
            edges {
              node {
                id
                fields {
                  key
                  value
                  reference {
                    ... on MediaImage {
                      image {
                        url
                        altText
                        width
                        height
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      let lastEntries = [];

      for (const type of typeCandidates) {
        const response = await fetch('/api/2023-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken
          },
          body: JSON.stringify({
            query,
            variables: { type }
          })
        });

        if (!response.ok) {
          throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        const entries = data.data?.metaobjects?.edges?.map((edge) => edge.node) || [];
        lastEntries = entries;

        if (entries.length > 0) {
          return entries;
        }
      }

      return lastEntries;
    }
  };
};

export default {
  name: 'boutiquesBlock',
  component: boutiquesBlock
};
